// =============================================================================
// MeuDriver — StudentPremiumCard
// Card de status da assinatura Premium do aluno com botão de cancelar.
// =============================================================================

import { Crown, CalendarDays, Loader2, AlertTriangle } from 'lucide-react'
import { format, parseISO } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { useSubscription, useCancelSubscription } from '@/hooks/useSubscription'

const STATUS_LABEL: Record<string, string> = {
  active: 'Ativa',
  cancelled: 'Cancelada',
  past_due: 'Pagamento pendente',
  expired: 'Expirada',
}

const STATUS_VARIANT: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> = {
  active: 'default',
  cancelled: 'secondary',
  past_due: 'destructive',
  expired: 'outline',
}

export function StudentPremiumCard() {
  const { data: subscription, isLoading } = useSubscription()
  const cancelSubscription = useCancelSubscription()

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Crown className="h-4 w-4 text-amber-500" />
            Assinatura Premium
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-16 animate-pulse rounded-md bg-gray-100" />
        </CardContent>
      </Card>
    )
  }

  if (!subscription) {
    return null
  }

  const renewalDate = subscription.current_period_end
    ? format(parseISO(subscription.current_period_end), "dd 'de' MMMM 'de' yyyy", { locale: ptBR })
    : '—'
  // Cancelada pelo usuário, mas com período pago vigente (benefício segue até o fim).
  const cancelada = subscription.status === 'active' && Boolean(subscription.cancelled_at)

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Crown className="h-4 w-4 text-amber-500" />
          Assinatura Premium
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">Status</span>
          <Badge variant={cancelada ? 'secondary' : STATUS_VARIANT[subscription.status] ?? 'outline'}>
            {cancelada ? 'Cancelada' : STATUS_LABEL[subscription.status] ?? subscription.status}
          </Badge>
        </div>

        {subscription.status === 'active' && (
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground flex items-center gap-1">
              <CalendarDays className="h-3.5 w-3.5" />
              {cancelada ? 'Acesso até' : 'Próxima renovação'}
            </span>
            <span className="text-sm font-medium">{renewalDate}</span>
          </div>
        )}

        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">Valor</span>
          <span className="text-sm font-medium">
            R$ {subscription.plan_price.toFixed(2).replace('.', ',')}/mês
          </span>
        </div>

        {subscription.status === 'active' && !cancelada && (
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="w-full text-destructive border-destructive/30 hover:bg-destructive/5"
                data-testid="cancel-premium-btn"
              >
                {cancelSubscription.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <>
                    <AlertTriangle className="h-3.5 w-3.5 mr-1.5" />
                    Cancelar plano
                  </>
                )}
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Cancelar assinatura Premium?</AlertDialogTitle>
                <AlertDialogDescription>
                  Você perderá os benefícios Premium (10% de desconto e prioridade na lista de
                  espera) ao final do período já pago — até lá tudo continua funcionando e não haverá nova
                  cobrança. Cancelando em até 7 dias da primeira cobrança, você recebe o valor
                  integral de volta e o plano termina na hora — desde que ainda não tenha
                  aulas agendadas ou realizadas no período.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Manter assinatura</AlertDialogCancel>
                <AlertDialogAction
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  onClick={() => cancelSubscription.mutate()}
                >
                  Confirmar cancelamento
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
      </CardContent>
    </Card>
  )
}
